import React from "react";
import PropTypes from "prop-types";
import { useOverlayTriggerState } from "@react-stately/overlays";
import {
  useOverlay,
  usePreventScroll,
  useModal,
  OverlayProvider,
  OverlayContainer,
} from "@react-aria/overlays";
import { useDialog } from "@react-aria/dialog";
import { FocusScope } from "@react-aria/focus";
import { AnimatePresence, motion } from "framer-motion";

const missingDialogProvider = "Component must be used within <Dialog.Root>";
const DialogContext = React.createContext({
  get open() {
    throw new Error(missingDialogProvider);
  },
});
DialogContext.displayName = "DialogContext";

function Overlay({ children }) {
  const { underlayProps } = React.useContext(DialogContext);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.3, ease: "easeInOut" } }}
      exit={{ opacity: 0, transition: { duration: 0.2, ease: "easeIn" } }}
      className="fixed inset-0 z-10 bg-black bg-opacity-75 flex items-center justify-center h-screen px-sm"
      {...underlayProps}
    >
      {children}
    </motion.div>
  );
}

export function Root({ children, isOpen, isDismissable, onClose }) {
  const ref = React.useRef(null);
  const { dialogProps, titleProps } = useDialog({ role: "dialog" }, ref);
  const { overlayProps, underlayProps } = useOverlay(
    {
      isOpen,
      onClose,
      isDismissable,
      isKeyboardDismissDisabled: isDismissable === false,
    },
    ref,
  );
  usePreventScroll({ isDisabled: !isOpen });

  return (
    <AnimatePresence>
      {isOpen ? (
        <DialogContext.Provider
          // eslint-disable-next-line react/jsx-no-constructed-context-values
          value={{
            isOpen,
            ref,
            dialogProps,
            titleProps,
            overlayProps,
            underlayProps,
          }}
        >
          <OverlayProvider>
            <OverlayContainer>
              <Overlay>
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.3, ease: "easeInOut" },
                  }}
                  exit={{
                    opacity: 0,
                    y: 16,
                    transition: { duration: 0.2, ease: "easeIn" },
                  }}
                >
                  {children}
                </motion.div>
              </Overlay>
            </OverlayContainer>
          </OverlayProvider>
        </DialogContext.Provider>
      ) : null}
    </AnimatePresence>
  );
}

export function Content({ children, className }) {
  const { ref, dialogProps, overlayProps } = React.useContext(DialogContext);
  const { modalProps } = useModal();

  return (
    <FocusScope contain restoreFocus autoFocus>
      <div
        {...overlayProps}
        {...dialogProps}
        {...modalProps}
        ref={ref}
        className={className}
      >
        {children}
      </div>
    </FocusScope>
  );
}

export function Title({ title, className }) {
  const { titleProps } = React.useContext(DialogContext);

  return (
    <h2 {...titleProps} className={className}>
      {title}
    </h2>
  );
}

export { useOverlayTriggerState };

Overlay.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

Overlay.defaultProps = {};

Root.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  isOpen: PropTypes.bool,
  isDismissable: PropTypes.bool,
  onClose: PropTypes.func,
};

Root.defaultProps = {
  isOpen: false,
  isDismissable: true,
  onClose: () => {},
};

Content.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  className: PropTypes.string,
};

Content.defaultProps = {
  className: undefined,
};

Title.propTypes = {
  title: PropTypes.string.isRequired,
  className: PropTypes.string,
};

Title.defaultProps = {
  className: undefined,
};
